import { Box } from '@mui/material'
import isEmpty from 'lodash/isEmpty'
import type { HTMLAttributes } from 'react'
import { FormattedMessage } from 'react-intl'

import { Maybe, SearchResultCase, SearchResultItem } from '@acre/graphql'
import { CaseStatusTag } from '@acre/design-system'

import CaseSummary from './CaseSummary'
import { getProductTypeIntl, reduceActiveInactiveCases } from './GlobalSearchOption.helpers'
import { BoxDetails, BoxName, BoxReason, Container } from './GlobalSearchOption.styles'

export const DEFAULT_EMPTY_OPTION = '-'

/**
 * Main GlobalSearchOption component
 *  - handles the case when option is empty and renders a "not found" message
 *  - otherwise renders the GlobalSearchResultOption when an option exist
 */
const GlobalSearchOption = (props: HTMLAttributes<HTMLLIElement>, option: any): JSX.Element => {
  if (isEmpty(option)) {
    return (
      <Box key="option-no-results">
        <FormattedMessage id="generic.nothingToShow" />
      </Box>
    )
  }
  return <GlobalSearchResultOption {...props} {...option} key={`option-${props.id}`} />
}

/**
 * Renders the active and inactive cases information within the search results
 */
const GlobalSearchResultOption = (props: SearchResultItem & { override_name?: string; focused?: boolean }) => {
  const { Cases: cases, organisation_name, first_name, last_name, postcode, override_name, focused, ...rest } = props

  // removes cases with InvalidStatus
  const [activeCases, inactiveCases] = reduceActiveInactiveCases(cases ?? [])
  const activeAndInactiveCases = [...activeCases, ...inactiveCases]

  return (
    <Container {...rest} focused={focused}>
      <BoxName>{override_name ?? getName(organisation_name, first_name, last_name)}</BoxName>
      <BoxReason>{getReason(activeCases, inactiveCases)}</BoxReason>
      <BoxDetails>
        <Box className="status-tag" data-testid="status-tag">
          {getStatus(activeAndInactiveCases)}
        </Box>
        <Box className="postcode">{postcode ?? DEFAULT_EMPTY_OPTION}</Box>
      </BoxDetails>
    </Container>
  )
}

/**
 * Determines what to display in the reason field based on number of cases
 *  - for single case: shows the case reason
 *  - for multiple cases: shows a summary component
 *  - for no cases: shows a placeholder
 */
const getReason = (activeCases: SearchResultCase[], inactiveCases: SearchResultCase[]) => {
  const cases = [...activeCases, ...inactiveCases]
  if (cases.length === 1) {
    const caseObj = cases[0]
    if (caseObj?.case_reason) {
      return <FormattedMessage id={getProductTypeIntl(caseObj?.case_reason)} />
    }
  }

  if (cases.length > 1) {
    return <CaseSummary activeCases={activeCases} inactiveCases={inactiveCases} />
  }

  return DEFAULT_EMPTY_OPTION
}

const getStatus = (cases: SearchResultCase[]): JSX.Element | null => {
  if (cases.length === 1 && cases[0]?.case_status) {
    return <CaseStatusTag status={cases[0].case_status} key={`status_tag_${cases[0]?.id}`} />
  }
  return null
}

const getName = (orgName: Maybe<string>, firstName: Maybe<string>, lastName: Maybe<string>) => {
  if (orgName) return orgName

  if (firstName && lastName) return `${firstName} ${lastName}`
  if (firstName) return firstName
  if (lastName) return lastName

  return DEFAULT_EMPTY_OPTION
}

export default GlobalSearchOption
